import { useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  calculateAgeYear,
  formatHealth,
  formatToYYYY_MMM_DD,
} from "../../../helpers/functions";
import { getCurrentUserDetails } from "../../../redux/modules/user/actions";
import { ClinicalServiceTypes } from "../../patient/text/data";
import {
  Container,
  DataField,
  Divider,
  H2,
  P,
  Section,
} from "../components/common-components";
import { FormHeader } from "../components/FormHeader";
import { FormContextMA } from "../form-context";
import { FormFooter } from "../components/FormFooter";
import {
  biologicalSexOptions,
  genderOptions,
  raceOptions,
} from "../../patient/create/options";
import { GetDropDownValue } from "../components/data";
import { GetPatientAddress } from "../common/GetPatientAddress";

export const PatientReferralForm = ({}) => {
  const { patient, clinicalServiceName, startDateTime, treatment, formType } =
    useContext(FormContextMA);
  
  const { currentLoggedInUserDetail } = useSelector((state) => state.currentLoggedInUserDetail);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();

  // Helper to compute pharmacist full name once
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  // Helper to format signature URL
  const getSignatureUrl = (u) => {
    if (!u?.signature || typeof u.signature !== 'string' || u.signature.length === 0) return null;
    return u.signature.startsWith('data:') ? u.signature : `data:image/png;base64,${u.signature}`;
  };
  const pharmacistSignature =
    getSignatureUrl(currentLoggedInUserDetail) ??
    getSignatureUrl(usersState?.currentLoggedInUserDetail) ??
    null;

  // Helper to get license number
  const getLicenseNumber = (u) => {
    if (!u?.driverLicence || typeof u.driverLicence !== 'string' || u.driverLicence.trim().length === 0) return "";
    return u.driverLicence.trim();
  };
  const pharmacistLicenseNumber =
    getLicenseNumber(currentLoggedInUserDetail) ||
    getLicenseNumber(usersState?.currentLoggedInUserDetail) ||
    "";

  // Fetch user details if not available
  useEffect(() => {
    if (!currentLoggedInUserDetail) {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const userId = userInfo.userId;
      
      if (userId) {
        dispatch(getCurrentUserDetails(userId, false));
      }
    }
  }, [currentLoggedInUserDetail, dispatch]);

  return (
    <Container>
      <FormHeader formType={formType} />
      {/* patient details */}
      <div className="min-h-[780px]">
        <div className="px-9 py-4">
          <div className="flex flex-row justify-between font-medium">
            <DataField
              value={`Minor Ailment Prescribing Service - Allergic Rhinitis`}
              className={"!text-[16px]"}
            />
            <P className="!text-[16px]">
              <span className="text-REELTeal">Service Date</span>{" "}
              {formatToYYYY_MMM_DD(startDateTime)}
            </P>
          </div>
          <div className="flex flex-col gap-2"></div>
        </div>
        {/* patient details */}
        <div className="px-9 py-4 flex flex-col gap-20 font-medium min-h-[368px]">
          <Section className="space-y-1" formType = {formType}>
            <H2>Patient Information</H2>
            <div className="grid grid-cols-1">
              <DataField
                label="Patient Name"
                value={(
                  (patient.firstName || "") +
                  (patient.middleName ? ` ${patient.middleName}` : "") +
                  (patient.lastName ? ` ${patient.lastName}` : "")
                ).trim()}
              />
              <DataField
                label="Health Card Number"
                value={
                  patient?.provincialInsurance?.cardNumber
                    ? `${formatHealth(
                        patient.provincialInsurance.cardNumber
                      )}-${patient.provincialInsurance.versionCode}`
                    : "XXX-XXX-XXX-EF"
                }
              />
            </div>
            <Divider />
            <div className="grid grid-cols-3">
              <DataField
                label="Date Of Birth"
                value={
                  patient.dateOfBirth
                    ? formatToYYYY_MMM_DD(patient.dateOfBirth)
                    : ""
                }
              />
              <DataField
                label="Age"
                value={
                  patient.dateOfBirth
                    ? calculateAgeYear(patient.dateOfBirth)
                    : ""
                }
              />
            </div>
            <GetPatientAddress patient={patient} showLabel={true} vertical={false} />
          </Section>
          <div className="space-y-6">
            <DataField
              value={`To: ${(
                (patient.firstName || "") +
                (patient.middleName ? ` ${patient.middleName}` : "") +
                (patient.lastName ? ` ${patient.lastName}` : "")
              ).trim()}`}
            />
            <DataField
              label={`This document confirms a Minor Ailment Service for ${
                clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
                  ? "Allergic Rhinitis"
                  : "Annual Medication Review"
              } was provided at ${"Pharmacy Name"} , ${"Store#"}. As a result of the Assessment, the pharmacist advises the patient to seek medical attention for further evaluation.`}
            />
          </div>
        </div>
        {/* pharmacist details */}
        <div className="px-9 py-20 flex flex-col gap-[0.114167in]">
          <DataField
            vertical={true}
            labelTeal={true}
            label={"Prescribing Pharmacist Name"}
            value={pharmacistName}
          />
          <DataField
            vertical={true}
            labelTeal={true}
            label={"Pharmacist Signature"}
            value={
              pharmacistSignature ? (
                <img
                  src={pharmacistSignature}
                  alt="Pharmacist Signature"
                  className="max-h-[40px] max-w-[200px] object-contain align-middle"
                />
              ) : (
                ""
              )
            }
          />
          <DataField
            vertical={true}
            labelTeal={true}
            label={"Pharmacist License"}
            value={pharmacistLicenseNumber}
          />
        </div>
      </div>
      <FormFooter />
    </Container>
  );
};
